/**
 * Measuring and shaping what goes on a flashcard.
 *
 * A card used to hold one letter, rendered at a fixed `min(52vh, 52vw)`. That
 * is correct for "M" and wrong for everything a teacher actually asked for
 * next: eight ducks to count, or a short phrase to read. At a fixed size those
 * run off the card, and the child is asked about something they cannot see.
 *
 * The sizing itself has to be measured rather than guessed — glyph widths vary
 * enormously between a letter, an emoji and a full stop — so the part that can
 * be pure and tested is the DECISION made from a measurement, plus the
 * counting, which is where emoji quietly break naive code.
 */

/**
 * Split into what a reader would call characters.
 *
 * `Array.from` handles surrogate pairs, so 🦆 counts as one. It does NOT
 * handle zero-width-joiner sequences: 👨‍👩‍👧 is three code points joined, and
 * Array.from reports 3. Intl.Segmenter is the only thing that gets that right,
 * and it is absent in older Safari — hence the fallback rather than a hard
 * dependency.
 */
export function graphemes(text: string): string[] {
  const s = typeof text === 'string' ? text : '';
  if (!s) return [];
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: object) => { segment(x: string): Iterable<{ segment: string }> } }).Segmenter;
  if (Seg) {
    try {
      return [...new Seg(undefined, { granularity: 'grapheme' }).segment(s)].map(g => g.segment);
    } catch { /* fall through */ }
  }
  return Array.from(s);
}

export const graphemeCount = (text: string): number => graphemes(text).length;

/** Rough "is this picture-content rather than words". */
const EMOJI_RE = /\p{Extended_Pictographic}/u;

/**
 * True when every visible grapheme is a picture.
 *
 * Drives two decisions: picture content must NOT take the letterform font
 * (LUC Print is for letters and has no bearing on an emoji), and a row of
 * identical pictures is something a child COUNTS, which wants different
 * spacing from a word they read.
 */
export function isPictureOnly(text: string): boolean {
  const gs = graphemes(text).filter(g => g.trim() !== '');
  if (gs.length === 0) return false;
  return gs.every(g => EMOJI_RE.test(g));
}

/**
 * How wide each printable ASCII character is in LUC Print Bold, in thousandths of
 * an em, from U+0020 up.
 *
 * MEASURED FROM THE SHIPPED FONT FILE (public/fonts/luc-print-700.woff2),
 * not estimated. The first version used a single "safe ceiling" of 0.65em per
 * character and was wrong in both directions, badly enough to clip cards:
 *
 *   "mummy"  needs 3.72em, assumed 3.25 — 15% over, clipped at both ends
 *   "WOW"    needs 2.82em, assumed 1.95 — 44% over, badly clipped
 *   "the cat sat on the mat" needs 10.67em, assumed 14.30 — shrunk for nothing
 *
 * Lowercase runs 0.31em (i) to 0.86em (m) and uppercase reaches 1.04em (W), so
 * no single constant can be both safe for "WOW" and honest for a sentence.
 * Summing real advances keeps the no-measurement decision — this is a table
 * lookup, not a layout read — while being correct.
 */
const LUC_PRINT_BOLD_ADVANCE = "269,381,530,605,605,764,662,305,420,420,508,586,325,437,347,527,605,605,605,605,605,605,605,605,605,605,347,347,586,586,586,532,851,725,676,676,725,592,586,719,740,510,503,698,552,906,754,732,623,754,674,607,618,728,715,1042,667,666,610,415,527,415,430,632,312,613,593,499,618,542,391,579,603,312,327,556,312,859,608,566,603,591,486,505,432,608,539,767,579,537,518,508,337,508,544".split(',').map(Number);

/**
 * U+00A0–U+00FF, the same way. The font ships all 96 of them, and Ontario
 * French immersion puts é, è, à, ç and ô on ordinary cards — counting each as
 * a full em made "Wörter" render 11% smaller than it needed to be.
 */
const LATIN1_ADVANCE = "269,381,532,605,605,605,520,535,410,750,396,638,586,437,754,405,349,586,410,410,312,608,540,366,205,410,391,638,843,843,843,532,725,725,725,725,725,725,894,676,592,592,592,592,510,510,510,510,732,754,732,732,732,732,732,586,732,728,728,728,728,666,623,694,613,613,613,613,613,613,868,499,542,542,542,542,315,315,315,315,568,608,566,566,566,566,566,586,566,608,608,608,608,537,599,537".split(',').map(Number);

/** Wide scripts advance a full em and have no spaces to wrap at. */
const FULL_WIDTH = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}\uFF00-\uFFEF]/u;

/**
 * One grapheme's width in em.
 *
 * Unknown characters are assumed FULL WIDTH. Over-shrinking an unusual glyph
 * costs a little whitespace; under-estimating it clips the card, and this tool
 * serves ESL classes where Latin-Extended and CJK are ordinary content.
 */
export function advanceEm(grapheme: string): number {
  if (!grapheme) return 0;
  if (isPictureOnly(grapheme)) return 1.15;       // emoji are near-monospace
  if (FULL_WIDTH.test(grapheme)) return 1;
  const cp = grapheme.codePointAt(0)!;
  if (cp >= 0x20 && cp <= 0x7e) return LUC_PRINT_BOLD_ADVANCE[cp - 0x20] / 1000;
  if (cp >= 0xa0 && cp <= 0xff) return LATIN1_ADVANCE[cp - 0xa0] / 1000;
  return 1;
}

/** Total width of a string in em, at font size 1. */
export function widthEm(text: string): number {
  return graphemes(text).reduce((sum, g) => sum + advanceEm(g), 0);
}

/** Past this, nobody is counting — and no taught arrangement goes further. */
export const MAX_COUNTING_REPEAT = 20;

export interface CardSizeInput {
  text: string;
  /** Viewport, in the units the caller will use (vmin is derived from these). */
  vw: number;
  vh: number;
}

export interface CardSize {
  /** Font size in px. */
  fontPx: number;
  lineHeight: number;
  /** Words must never break mid-word — that teaches wrong reading. */
  whiteSpace: 'nowrap' | 'normal';
}

export function cardSize({ text, vw, vh }: CardSizeInput): CardSize {
  const vmin = Math.min(vw, vh);
  const gs = graphemes(text).filter(g => g !== '');
  const n = gs.length;
  const MAX = vmin * 0.52;
  const FLOOR = vmin * 0.07;
  if (n === 0) return { fontPx: MAX, lineHeight: 1, whiteSpace: 'nowrap' };

  const words = text.trim().split(/\s+/).filter(Boolean);
  const usableW = vw * 0.88;
  const usableH = vh * 0.82;

  /**
   * ONE glyph keeps the size the tool has always used, so a letter still fills
   * the card. Everything else is sized from REAL advances — see
   * LUC_PRINT_BOLD_ADVANCE for why a single per-character constant could not be
   * both safe for "WOW" and honest for a sentence.
   */
  if (n === 1) return { fontPx: MAX, lineHeight: 1, whiteSpace: 'nowrap' };

  /**
   * Wide scripts have no spaces to wrap at, so a CJK phrase is one unbreakable
   * run however many words a reader would call it.
   */
  const unbreakable = words.length <= 1 || FULL_WIDTH.test(text);

  if (unbreakable) {
    const byWidth = usableW / widthEm(text);
    return {
      fontPx: Math.max(FLOOR, Math.min(MAX, byWidth)),
      lineHeight: 1,
      whiteSpace: 'nowrap',
    };
  }

  /**
   * A phrase is bounded twice: the longest WORD must fit one line unbroken —
   * breaking a word mid-syllable teaches wrong reading — and the whole thing
   * must fit the HEIGHT once wrapped.
   */
  const LINE = 1.15;
  const byLongestWord = usableW / Math.max(...words.map(widthEm));
  let font = Math.min(MAX, byLongestWord);

  /**
   * Count the lines by actually wrapping, then shrink until they fit.
   *
   * The previous version estimated height from total text AREA, which assumes
   * fractional lines and perfect packing. Real wrapping is quantised: whole
   * lines, broken at word boundaries. For ordinary two-to-four-word phrases
   * that under-counts by a full line or more — "the cat sat", the placeholder's
   * own example, came out three lines tall and pushed the key hints and
   * Back/Next clean off the bottom of a container that cannot scroll.
   *
   * Monotone (each pass only shrinks) so it converges; two or three passes is
   * the normal case and the cap is a backstop, not an expectation.
   */
  for (let pass = 0; pass < 6; pass++) {
    const lines = wrapLineCount(words, usableW, font);
    const needed = lines * LINE * font;
    if (needed <= usableH) break;
    font = usableH / (lines * LINE);
  }

  return {
    fontPx: Math.max(FLOOR, Math.min(MAX, font)),
    lineHeight: LINE,
    whiteSpace: 'normal',
  };
}

/**
 * How many lines these words take at this size — greedy, the way a browser
 * wraps: fill a line until the next word will not fit, then start another.
 */
export function wrapLineCount(words: string[], usableW: number, fontPx: number): number {
  if (words.length === 0 || fontPx <= 0) return 1;
  const space = advanceEm(' ') * fontPx;
  let lines = 1;
  let cur = 0;
  for (const w of words) {
    const width = widthEm(w) * fontPx;
    if (cur === 0) cur = width;                       // first word on a line always goes
    else if (cur + space + width <= usableW) cur += space + width;
    else { lines++; cur = width; }
  }
  return lines;
}


export interface NewCard {
  displayValue: string;
  itemType: 'emoji' | 'phrase' | 'custom';
  meta?: { glyph: string; count: number };
}

/**
 * What the teacher meant by what they typed into the "add a card" box.
 *
 * Three shapes come through one input:
 *
 *   "8 🦆"        a COUNTING card — eight ducks in a ten-frame, labelled "8 🦆"
 *   "🦆"          one picture to name, at full size
 *   "the cat sat" a phrase, wrapped and sized to fit
 *
 * The count is parsed HERE rather than leaving emoji in displayValue, because
 * displayValue is also the printed chart's column header, the CSV column title
 * and the grid tooltip — and eight emoji is unusable in every one of those. On
 * the printed chart it is worse than unusable: vertical column headers render
 * emoji upright and then flip them, so they print upside down.
 *
 * Exported and used by the component rather than duplicated into it: a copy of
 * this logic in a test proves the copy works.
 */
export function parseNewCard(value: string): NewCard | null {
  const v = (value || '').trim();
  if (!v) return null;

  // "8 🦆" / "8🦆" / "8x🦆" / "8 × 🦆"
  const counted = v.match(/^(\d{1,2})\s*[x×*]?\s*(\S+)$/u);
  if (counted && isPictureOnly(counted[2])) {
    const glyph = graphemes(counted[2])[0];
    const count = Math.max(1, Math.min(Number(counted[1]) || 1, MAX_COUNTING_REPEAT));
    // "1 🦆" is a picture to NAME, not a count of one — a lone duck drawn at
    // counting-card scale is a tiny duck on an empty card.
    if (count === 1) return { displayValue: glyph, itemType: 'custom' };
    return { displayValue: `${count} ${glyph}`, itemType: 'emoji', meta: { glyph, count } };
  }

  // The same picture pasted repeatedly — same intent, typed differently.
  const gs = graphemes(v).filter(g => g.trim() !== '');
  if (gs.length > 1 && isPictureOnly(v) && gs.every(g => g === gs[0])) {
    const count = Math.min(gs.length, MAX_COUNTING_REPEAT);
    return { displayValue: `${count} ${gs[0]}`, itemType: 'emoji', meta: { glyph: gs[0], count } };
  }

  const isPhrase = v.split(/\s+/).length > 1;
  return { displayValue: v, itemType: isPhrase ? 'phrase' : 'custom' };
}
